import React, { useState, useEffect } from "react";
import { Row, Col, Form, FormControl } from "react-bootstrap";
import "./CreateProjectOfflineForm.css";
import { DatePicker, Select, message } from "antd";
import StbButton from "../../atoms/button/StbButton";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toastSuccess } from "../../../utilities/messageUtils";
import { useGetApiContactsCustomerCustomerNumber } from "../../../api/generated/endpoints/contact/contact";
import { getGetApiProjectsQueryKey, usePostApiProjects } from "../../../api/generated/endpoints/project/project";
import { useGetApiStbusersGetTeamleaders } from "../../../api/generated/endpoints/stbuser/stbuser";
import { useGetApiCustomers } from "../../../api/generated/endpoints/customer/customer";
import type {
  ContactSummaryDto,
  STBUser,
} from "../../../api/generated/model";
import { Department } from "../../../api/generated/model";
import ModalAddContact from "./ModalContact/ModalAddContact";
import ModalEditContact from "./ModalContact/ModalEditContact";
import { DEPARTMENT_OPTIONS } from "./departmentOptions";
import {
  scaffoldingStepHasErrors,
  validateScaffoldingStep,
} from "../../../validations/scaffoldingStepValidation";
import { toProjectCreateDto } from "../../pages/createproject/createProjectFields";

const CreateProjectOfflineForm: React.FC = () => {
  const [projectNumber, setProjectNumber] = useState<string>("");
  const [projectName, setProjectName] = useState("");
  const [projectAddress, setProjectAddress] = useState("");
  const [selectedContactIds, setSelectedContactIds] = useState<number[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState<number | undefined>();
  const [selectedProjectLeaderId, setSelectedProjectLeaderId] = useState<number | undefined>();
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(null);
  const [squareMeters, setSquareMeters] = useState<number>(0);
  const [accessRoutes, setAccessRoutes] = useState<number>(0);
  const [scaffoldingType, setScaffoldingType] = useState<string>("");
  const [scaffoldingIntendedFor, setScaffoldingIntendedFor] = useState<string>("");
  const [scaffoldingWidth, setScaffoldingWidth] = useState<string>("");
  const [scaffoldingHeight, setScaffoldingHeight] = useState<string>("");
  const [includeMonteringsanalys, setIncludeMonteringsanalys] = useState<boolean>(true);

  const [selectedCustomerId, setSelectedCustomerId] = useState<number | undefined>();
  const [customerContacts, setCustomerContacts] = useState<ContactSummaryDto[]>([]);

  const [isModalAddContactVisible, setIsModalAddContactVisible] = useState(false);
  const [isModalEditContactVisible, setIsModalEditContactVisible] = useState(false);
  const [addContactErrorMessage, setAddContactErrorMessage] = useState("");
  const [isContactListUpdated, setIsContactListUpdated] = useState(false);

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: customers, isLoading: isLoadingCustomers } = useGetApiCustomers({
    query: {
      staleTime: Infinity,
    },
  });

  const { data: teamLeaders, isLoading: isLoadingTeamLeaders } = useGetApiStbusersGetTeamleaders({
    query: {
      staleTime: Infinity,
    },
  });

  const selectedCustomerForContacts = customers?.find(c => c.id === selectedCustomerId);

  const {
    data: fetchedCustomerContacts,
    isLoading: isLoadingContacts,
    error: contactsError
  } = useGetApiContactsCustomerCustomerNumber(
    selectedCustomerForContacts?.customerNumber || 0,
    {
      query: {
        enabled: !!selectedCustomerForContacts?.customerNumber,
      },
    }
  );

  useEffect(() => {
    if (fetchedCustomerContacts) {
      setCustomerContacts(fetchedCustomerContacts);
    } else if (!selectedCustomerId) {
      setCustomerContacts([]);
    }
  }, [fetchedCustomerContacts, selectedCustomerId]);

  useEffect(() => {
    if (contactsError) {
      setCustomerContacts([]);
    }
  }, [contactsError]);

  useEffect(() => {
    if (isContactListUpdated && selectedCustomerForContacts?.customerNumber) {
      queryClient.invalidateQueries({ queryKey: ["customerContacts_offline", selectedCustomerForContacts.customerNumber] });
      setIsContactListUpdated(false);
    }
  }, [isContactListUpdated, selectedCustomerForContacts?.customerNumber, queryClient]);

  const projectMutation = usePostApiProjects({
    mutation: {
      onSuccess: async (createdProjectId) => {
        toastSuccess("Projekt skapat!");
        await queryClient.invalidateQueries({ queryKey: getGetApiProjectsQueryKey() });
        await queryClient.invalidateQueries({ queryKey: ["projectSelection"] });
        navigate("/projects");
      }
    }
  });

  const handleCustomerChange = (value: number | undefined) => {
    setSelectedCustomerId(value);
    setSelectedContactIds([]);
  };

  const handleChangeContactIds = (values: number[]) => {
    setSelectedContactIds(values);
  };

  const handleDateChange = (date: dayjs.Dayjs | null) => {
    setSelectedDate(date);
  };

  const handleAddContactClick = () => {
    if (!selectedCustomerForContacts?.customerNumber) {
      message.error("Välj en kund först för att lägga till kontakter.");
      return;
    }
    setAddContactErrorMessage("");
    setIsModalAddContactVisible(true);
  };

  const handleEditContactClick = () => {
    if (!selectedCustomerForContacts?.customerNumber) {
      message.error("Välj en kund först för att redigera kontakter.");
      return;
    }
    if (customerContacts.length === 0) {
      message.info("Det finns inga kontakter att redigera för vald kund.");
      return;
    }
    setAddContactErrorMessage("");
    setIsModalEditContactVisible(true);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!projectNumber.trim()) {
      message.error("Projektnummer måste anges.");
      return;
    }
    if (!selectedCustomerId) {
      message.error("Välj en kund.");
      return;
    }
    if (!projectName.trim()) {
      message.error("Skriv in ett Projektnamn.");
      return;
    }
    if (!projectAddress.trim()) {
      message.error("Skriv in en Projektadress.");
      return;
    }
    if (customerContacts.length > 0 && selectedContactIds.length === 0 && !projectMutation.isPending) {
    }
    if (!selectedDepartment) {
      message.error("Välj en avdelning.");
      return;
    }
    if (!selectedProjectLeaderId) {
      message.error("Välj en projektledare.");
      return;
    }
    if (!selectedDate) {
      message.error("Välj ett datum för uppförande.");
      return;
    }

    const scaffoldingErrors = validateScaffoldingStep({
      squareMeters,
      accessRoutes,
      scaffoldingType,
      scaffoldingIntendedFor,
      scaffoldingWidth,
      scaffoldingHeight,
    });
    if (scaffoldingStepHasErrors(scaffoldingErrors)) {
      const firstError = Object.values(scaffoldingErrors).find((error) => error.length > 0);
      if (firstError) {
        message.error(firstError);
      }
      return;
    }

    const activeContacts = customerContacts.filter((contact) =>
      contact.id !== undefined && selectedContactIds.includes(contact.id)
    );

    const dto = toProjectCreateDto({
      projectNumber: projectNumber.trim(),
      customerId: selectedCustomerId,
      department: selectedDepartment,
      projectName: projectName,
      projectAddress: projectAddress,
      teamLeaderId: selectedProjectLeaderId,
      contacts: activeContacts,
      dateOfConstructionStart: selectedDate.toDate().toISOString(),
      squareMeters: squareMeters,
      accessRoutes: accessRoutes,
      scaffoldingType: scaffoldingType,
      scaffoldingIntendedFor: scaffoldingIntendedFor,
      scaffoldingWidth: scaffoldingWidth,
      scaffoldingHeight: scaffoldingHeight,
      hasMonteringsanalys: includeMonteringsanalys,
    });
    projectMutation.mutate({ data: dto });
  };

  // Add useEffect to handle mobile dropdown scrolling
  useEffect(() => {
    const handleTouchStart = (e: TouchEvent) => {
      const target = e.target as HTMLElement;
      // Check if touch started on any select component or its dropdown
      const isSelectElement = target.closest('.ant-select, .ant-select-dropdown, .dropdown-menu');
      if (isSelectElement) {
        (e as any).isDropdownTouch = true;
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      // If touch started on dropdown-related element, prevent page scroll
      if ((e as any).isDropdownTouch) {
        e.stopPropagation();
      }
    };

    // Use passive listeners to avoid cancelable warnings
    document.addEventListener('touchstart', handleTouchStart, { passive: false });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
    };
  }, []);

  // Simplified getPopupContainer function
  const getPopupContainer = (trigger: HTMLElement) => {
    return trigger.parentElement as HTMLElement;
  };

  return (
    <>
      {isModalAddContactVisible && selectedCustomerForContacts?.customerNumber && (
        <ModalAddContact
          customerNumber={selectedCustomerForContacts.customerNumber}
          isModalAddContactVisible={isModalAddContactVisible}
          setIsModalAddContactVisible={setIsModalAddContactVisible}
          setIsContactListUpdated={setIsContactListUpdated}
          customerContactList={customerContacts}
          setCustomerContactList={setCustomerContacts}
          setAddContactErrorMessage={setAddContactErrorMessage}
          setShowConfirmationMessage={() => { }}
          selectedContactsIds={selectedContactIds}
          setSelectedContactsIds={setSelectedContactIds}
        />
      )}
      {isModalEditContactVisible && selectedCustomerForContacts?.customerNumber && (
        <ModalEditContact
          customerContactList={customerContacts}
          customerNumber={selectedCustomerForContacts.customerNumber}
          setCustomerContactList={setCustomerContacts}
          isModalEditContactVisible={isModalEditContactVisible}
          setIsModalEditContactVisible={setIsModalEditContactVisible}
          setIsContactListUpdated={setIsContactListUpdated}
          selectedContactsIds={selectedContactIds}
          setSelectedContactsIds={setSelectedContactIds}
        />
      )}
      <Row className="d-flex justify-content-center">
        <Col xl={6} lg={6} md={8} sm={12} xs={12}>
          <Row className="d-flex justify-content-center align-items-center">
          </Row>
          <Form onSubmit={handleSubmit} className="text-start">
            <Row className="my-3">
              <Col>
                <Form.Label htmlFor="projectNumberInput">PROJEKTNR</Form.Label>
                <FormControl
                  id="projectNumberInput"
                  type="text"
                  placeholder="Skriv in projektnumret"
                  value={projectNumber}
                  onChange={(e) => setProjectNumber(e.target.value)}
                  required
                />
              </Col>
            </Row>

            <Row className="my-3">
              <Col>
                <Form.Label htmlFor="customerSelect">BESTÄLLARE</Form.Label>
                <Select
                  id="customerSelect"
                  showSearch
                  allowClear
                  style={{ width: "100%" }}
                  placeholder="Välj kund"
                  loading={isLoadingCustomers}
                  onChange={handleCustomerChange}
                  value={selectedCustomerId}
                  getPopupContainer={getPopupContainer}
                  dropdownStyle={{ zIndex: 9999 }}
                  filterOption={(input, option) =>
                    (option?.label?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={customers?.map(customer => ({ label: customer.name, value: customer.id }))}
                />
              </Col>
            </Row>

            <Row className="my-3">
              <Col>
                <Form.Label htmlFor="projectNameInput">PROJEKTNAMN</Form.Label>
                <FormControl
                  id="projectNameInput"
                  type="text"
                  placeholder="Skriv in projektets namn"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  required
                />
              </Col>
            </Row>

            <Row className="my-3">
              <Col>
                <Form.Label htmlFor="projectAddressInput">PROJEKTADRESS</Form.Label>
                <FormControl
                  id="projectAddressInput"
                  type="text"
                  placeholder="Skriv in adressen"
                  value={projectAddress}
                  onChange={(e) => setProjectAddress(e.target.value)}
                  required
                />
              </Col>
            </Row>

            <Row className="my-3">
              <Col>
                <Row className="mb-1">
                  <Col xs={12} md>
                    <Form.Label htmlFor="contactPersonSelect" className="mb-0 align-middle">KONTAKTPERSON</Form.Label>
                  </Col>
                  <Col xs={12} md="auto" className="d-flex justify-content-md-end mt-2 mt-md-0">
                    <StbButton
                      variant="outline"
                      onClick={handleAddContactClick}
                      className="me-2"
                      disabled={!selectedCustomerId}
                    >
                      Lägg till kontakt
                    </StbButton>
                    <StbButton
                      variant="outline"
                      onClick={handleEditContactClick}
                      disabled={!selectedCustomerId || customerContacts.length === 0}
                    >
                      Ändra kontakt
                    </StbButton>
                  </Col>
                </Row>
                <Select
                  id="contactPersonSelect"
                  mode="multiple"
                  showSearch
                  allowClear
                  style={{ width: "100%" }}
                  placeholder={!selectedCustomerId ? "Välj kund först" : (isLoadingContacts ? "Laddar kontakter..." : "Välj kontaktpersoner")}
                  loading={isLoadingContacts}
                  value={selectedContactIds}
                  onChange={handleChangeContactIds}
                  getPopupContainer={getPopupContainer}
                  dropdownStyle={{ zIndex: 9999 }}
                  filterOption={(input, option) =>
                    (option?.label?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={customerContacts.map((contact) => ({
                    label: `${contact.name} (${(contact.phoneNumbers || []).join(", ") || '-'}) ${(contact.emailAddresses || []).join(", ") || '-'}`,
                    value: contact.id,
                  }))}
                  disabled={!selectedCustomerId || isLoadingContacts}
                />
                {addContactErrorMessage && (
                  <div className="text-danger mt-1">{addContactErrorMessage}</div>
                )}
                {selectedCustomerId && !isLoadingContacts && customerContacts.length === 0 && (
                  <small className="text-muted">Inga kontakter hittades för vald kund.</small>
                )}
              </Col>
            </Row>

            <Row className="my-3">
              <Col>
                <Form.Label htmlFor="departmentSelect">AVDELNING</Form.Label>
                <Select
                  id="departmentSelect"
                  style={{ width: "100%" }}
                  placeholder="Välj Avdelning"
                  onChange={(value) => setSelectedDepartment(value as Department)}
                  value={selectedDepartment}
                  getPopupContainer={getPopupContainer}
                  dropdownStyle={{ zIndex: 9999 }}
                  options={DEPARTMENT_OPTIONS.map((departmentOption) => ({
                    value: departmentOption.value,
                    label: `${departmentOption.value}. ${departmentOption.label}`,
                  }))}
                />
              </Col>
            </Row>

            <Row className="my-3">
              <Col>
                <Form.Label htmlFor="projectLeaderSelect">ARBETSLEDARE</Form.Label>
                <Select
                  id="projectLeaderSelect"
                  showSearch
                  style={{ width: "100%" }}
                  placeholder="Välj Projektledare"
                  loading={isLoadingTeamLeaders}
                  onChange={(value) => setSelectedProjectLeaderId(value)}
                  value={selectedProjectLeaderId}
                  getPopupContainer={getPopupContainer}
                  dropdownStyle={{ zIndex: 9999 }}
                  filterOption={(input, option) =>
                    (option?.label?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={(teamLeaders as STBUser[] | undefined)?.map((leader: STBUser) => ({ label: leader.name, value: leader.id }))}
                />
              </Col>
            </Row>

            <Row className="my-3">
              <Col>
                <Form.Label htmlFor="constructionDateInput">STARTDATUM</Form.Label>
                <DatePicker
                  id="constructionDateInput"
                  onChange={handleDateChange}
                  value={selectedDate}
                  size="large"
                  className="custom-date-picker"
                  placeholder="Välj datum"
                  style={{ width: "100%" }}
                />
              </Col>
            </Row>

            <Row className="my-3">
              <Col md={6}>
                <Form.Label htmlFor="squareMetersInput">ANTAL KVADRATMETER (m²)</Form.Label>
                <FormControl
                  id="squareMetersInput"
                  type="number"
                  min="0"
                  value={squareMeters || ''}
                  onChange={(e) => setSquareMeters(e.target.value ? Number(e.target.value) : 0)}
                />
              </Col>
              <Col md={6}>
                <Form.Label htmlFor="accessRoutesInput">ANTAL TILLTRÄDESLEDER</Form.Label>
                <FormControl
                  id="accessRoutesInput"
                  type="number"
                  min="0"
                  value={accessRoutes || ''}
                  onChange={(e) => setAccessRoutes(e.target.value ? Number(e.target.value) : 0)}
                />
              </Col>
            </Row>
            <Row className="my-3">
              <Col>
                <Form.Label>MONTERINGSANALYS</Form.Label>
                <div className="d-flex gap-3">
                  <Form.Check
                    type="checkbox"
                    label="JA"
                    checked={includeMonteringsanalys === true}
                    onChange={() => setIncludeMonteringsanalys(true)}
                  />
                  <Form.Check
                    type="checkbox"
                    label="NEJ"
                    checked={includeMonteringsanalys === false}
                    onChange={() => setIncludeMonteringsanalys(false)}
                  />
                </div>
              </Col>
            </Row>
            <Row className="my-3">
              <Col>
                <Form.Label>TYP AV STÄLLNING</Form.Label>
                <FormControl
                  type="text"
                  placeholder=""
                  value={scaffoldingType}
                  onChange={(e) => setScaffoldingType(e.target.value)}
                />
              </Col>
            </Row>

            <Row className="my-3">
              <Col>
                <Form.Label>STÄLLNING AVSEDD FÖR TYP AV ARBETE</Form.Label>
                <FormControl
                  type="text"
                  placeholder=""
                  value={scaffoldingIntendedFor}
                  onChange={(e) => setScaffoldingIntendedFor(e.target.value)}
                />
              </Col>
            </Row>

            <Row className="my-3">
              <Col md={6}>
                <Form.Label>STÄLLNINGSBREDD</Form.Label>
                <FormControl
                  type="text"
                  placeholder=""
                  value={scaffoldingWidth}
                  onChange={(e) => setScaffoldingWidth(e.target.value)}
                />
              </Col>
              <Col md={6}>
                <Form.Label>HÖJD</Form.Label>
                <FormControl
                  type="text"
                  placeholder=""
                  value={scaffoldingHeight}
                  onChange={(e) => setScaffoldingHeight(e.target.value)}
                />
              </Col>
            </Row>
            <Row className="d-flex justify-content-center align-items-center my-3">
              <Col xs="auto">
                <StbButton
                  className="mt-4"
                  type="submit"
                  loading={projectMutation.isPending}
                >
                  Skapa projekt
                </StbButton>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
    </>
  );
};

export default CreateProjectOfflineForm;
